// =============================================================================
// HOOK DE SYNCHRONISATION DE PÉRIODE - Rafraîchissement automatique à 13h, 17h et minuit
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { getCurrentWorkPeriod, type WorkPeriod } from '@/lib/disponibilite-utils';

/**
 * Hook qui synchronise les composants avec les changements de période de travail.
 *
 * Heures critiques :
 * - 13h00 : Fin de matinée (AM → PM)
 * - 17h00 : Fin de journée (PM → OFF)
 * - 00h00 : Nouveau jour (reset)
 *
 * @param onPeriodChange - Callback optionnel appelé quand la période change
 * @returns { period, refreshKey } - La période actuelle et une clé pour forcer le re-render
 *
 * @example
 * ```tsx
 * const { period, refreshKey } = usePeriodSync(() => {
 *   loadAffectationsActives();
 * });
 *
 * useEffect(() => {
 *   loadData();
 * }, [refreshKey]); // Se recharge automatiquement à 13h, 17h et minuit
 * ```
 */
export function usePeriodSync(onPeriodChange?: () => void) {
  const [period, setPeriod] = useState<WorkPeriod>(getCurrentWorkPeriod());
  const [refreshKey, setRefreshKey] = useState(0);
  const onPeriodChangeRef = useRef(onPeriodChange);

  // Mettre à jour la ref quand le callback change (évite les dépendances infinies)
  useEffect(() => {
    onPeriodChangeRef.current = onPeriodChange;
  }, [onPeriodChange]);

  useEffect(() => {
    const checkPeriod = () => {
      const newPeriod = getCurrentWorkPeriod();
      if (newPeriod !== period) {
        if (import.meta.env.DEV) console.log(`[usePeriodSync] Changement de période: ${period} → ${newPeriod}`);
        setPeriod(newPeriod);
        setRefreshKey(k => k + 1);
        onPeriodChangeRef.current?.();
      }
    };

    // Calculer le temps jusqu'au prochain changement (13h, 17h, ou minuit)
    const now = new Date();
    const hours = now.getHours();
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Timer pour 13h (si on est avant)
    if (hours < 13) {
      const target13 = new Date(now);
      target13.setHours(13, 0, 1, 0); // +1 seconde pour éviter les edge cases
      const ms = target13.getTime() - now.getTime();
      if (ms > 0) {
        if (import.meta.env.DEV) console.log(`[usePeriodSync] Timer 13h programmé dans ${Math.round(ms / 1000 / 60)} minutes`);
        timers.push(setTimeout(checkPeriod, ms));
      }
    }

    // Timer pour 17h (si on est avant)
    if (hours < 17) {
      const target17 = new Date(now);
      target17.setHours(17, 0, 1, 0);
      const ms = target17.getTime() - now.getTime();
      if (ms > 0) {
        if (import.meta.env.DEV) console.log(`[usePeriodSync] Timer 17h programmé dans ${Math.round(ms / 1000 / 60)} minutes`);
        timers.push(setTimeout(checkPeriod, ms));
      }
    }

    // Timer pour minuit (reset journalier)
    const targetMidnight = new Date(now);
    targetMidnight.setDate(targetMidnight.getDate() + 1);
    targetMidnight.setHours(0, 0, 1, 0);
    const msMidnight = targetMidnight.getTime() - now.getTime();
    if (import.meta.env.DEV) console.log(`[usePeriodSync] Timer minuit programmé dans ${Math.round(msMidnight / 1000 / 60)} minutes`);
    timers.push(setTimeout(checkPeriod, msMidnight));

    // Vérification de secours toutes les 30 secondes
    // (au cas où les timers précis ratent à cause de la mise en veille de l'onglet)
    const fallbackInterval = setInterval(checkPeriod, 30000);

    return () => {
      timers.forEach(t => clearTimeout(t));
      clearInterval(fallbackInterval);
    };
  }, [period]);

  return { period, refreshKey };
}
